import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import ProfileScreen from "@/components/mobile/ProfileScreen";
import { SaldoScreen, MutasiScreen } from "@/components/mobile/SaldoScreen";
import { toast } from "sonner";
import {
  Home, Fingerprint, Calendar, Banknote, Wallet, Clock, Star, FileText, ClipboardCheck, Bell, AlertTriangle,
  ChevronLeft, ChevronRight, LogOut, Loader2, Check, X, Camera, Users, LayoutGrid, Megaphone, BarChart3,
} from "lucide-react";

const BLUE = "#2563EB";
const GRAD = "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)";
const cardM = "bg-white rounded-2xl border border-slate-100 shadow-sm";
const inpM = "w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400";
const DAY_NAME = { sen: "Senin", sel: "Selasa", rab: "Rabu", kam: "Kamis", jum: "Jumat", sab: "Sabtu", min: "Minggu" };

function SubHeader({ title, onBack, testid }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-3 sticky top-0 z-30" style={{ background: "#F8FAFC" }} data-testid={testid}>
      <button data-testid={`${testid}-back`} onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><ChevronLeft className="w-5 h-5" /></button>
      <h1 className="flex-1 text-center font-bold text-slate-900 text-base pr-9">{title}</h1>
    </div>
  );
}

export default function MobileEmployee() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [screen, setScreen] = useState({ name: "dashboard" });
  const [today, setToday] = useState(null);
  const [myShift, setMyShift] = useState(null);
  const [dash, setDash] = useState(null);
  const [saldo, setSaldo] = useState(null);

  const reloadDash = useCallback(() => {
    api.get("/attendance/today-info").then((r) => setToday(r.data)).catch(() => {});
    api.get("/shifts/me").then((r) => setMyShift(r.data)).catch(() => {});
    api.get("/dashboard/me").then((r) => setDash(r.data)).catch(() => {});
    api.get("/saldo/me").then((r) => setSaldo(r.data?.saldo ?? 0)).catch(() => {});
  }, []);
  useEffect(() => { reloadDash(); }, [reloadDash]);

  const initials = (user?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const clockStatus = dash?.clock_status === "terlambat" ? `Terlambat ${dash.late_minutes} mnt` : dash?.clock_status === "hadir" ? "Tepat waktu" : null;
  const MENUS = [
    { label: "Absen", icon: Fingerprint, tid: "menu-absen", screen: "absen" },
    { label: "Izin", icon: FileText, tid: "menu-izin", screen: "izin" },
    { label: "Kasbon", icon: Banknote, tid: "menu-kasbon", screen: "kasbon" },
    { label: "Saldo", icon: Wallet, tid: "menu-gaji", screen: "saldo" },
    { label: "Lembur", icon: Clock, tid: "menu-lembur", screen: "lembur" },
    { label: "Shift", icon: Users, tid: "menu-shift", screen: "shift" },
    { label: "Tugas SPK", icon: ClipboardCheck, tid: "menu-spk", screen: "spk" },
    { label: "Lainnya", icon: LayoutGrid, tid: "menu-lainnya", screen: "lainnya" },
  ];
  const MORE = [
    { label: "Penilaian", icon: Star, tid: "menu-penilaian", screen: "penilaian" },
    { label: "Pengumuman", icon: Megaphone, tid: "menu-pengumuman", screen: "pengumuman" },
    { label: "Surat Peringatan", icon: AlertTriangle, tid: "menu-sp", screen: "sp" },
  ];

  return (
    <div className="min-h-screen max-w-md mx-auto" style={{ background: "#F1F5F9", fontFamily: "'Public Sans', sans-serif" }} data-testid="employee-app">
      {screen.name === "dashboard" && (
        <div data-testid="screen-dashboard" className="pb-24">
          {/* Header biru */}
          <div className="px-4 pt-5 pb-6" style={{ background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 60%, #1E40AF 100%)" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white font-black text-sm" style={{ fontFamily: "monospace" }}>K</span>
                <span className="text-white font-extrabold tracking-[0.2em] text-sm">KIYANA</span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="logout-btn" onClick={logout} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white"><LogOut className="w-4 h-4" /></button>
                <span className="w-10 h-10 rounded-full bg-white text-blue-700 flex items-center justify-center font-bold text-sm" data-testid="emp-initials">{initials}</span>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-blue-100 text-sm">Selamat pagi,</div>
              <div className="text-white font-extrabold text-2xl leading-tight" data-testid="emp-name">{user?.name}</div>
              <div className="text-blue-100 text-xs mt-0.5">{user?.role_label || "Karyawan"}</div>
            </div>
            {/* Saldo tersedia */}
            <button data-testid="dash-saldo-card" onClick={() => setScreen({ name: "saldo" })}
              className="mt-4 w-full rounded-2xl bg-white/10 border border-white/20 backdrop-blur px-4 py-3.5 text-left">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-blue-100 text-[11px]">Saldo tersedia</div>
                  <div className="text-white font-extrabold text-[22px] leading-tight mt-0.5" data-testid="dash-saldo">{formatIDR(saldo ?? 0)}</div>
                </div>
                <span className="h-9 px-4 rounded-xl bg-white text-blue-700 text-xs font-bold flex items-center gap-1">Lihat saldo <ChevronRight className="w-3.5 h-3.5" /></span>
              </div>
            </button>
          </div>

          {/* Jadwal + Absen masuk */}
          <div className="px-4 -mt-4">
            <div className={`${cardM} p-0 overflow-hidden grid grid-cols-2 divide-x divide-slate-100`}>
              <div className="p-4 flex items-start gap-2.5">
                <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Calendar className="w-4 h-4" /></span>
                <div className="min-w-0">
                  <div className="text-[10px] text-slate-400">Jadwal hari ini</div>
                  <div className="text-sm font-bold text-slate-900" data-testid="emp-shift-today">{today ? (today.is_workday ? `${today.shift_start} – ${today.shift_end}` : "LIBUR") : "—"}</div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start gap-2.5">
                  <span className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Clock className="w-4 h-4" /></span>
                  <div className="min-w-0">
                    <div className="text-[10px] text-slate-400">Absen masuk</div>
                    <div className="text-sm font-bold text-slate-900" data-testid="emp-clock-in">{dash?.clock_in ? formatTimeID(dash.clock_in) : "—"}</div>
                  </div>
                </div>
                {clockStatus && (
                  <span className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full ${dash.clock_status === "terlambat" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`} data-testid="dash-clock-status">
                    <span className={`w-1.5 h-1.5 rounded-full ${dash.clock_status === "terlambat" ? "bg-amber-500" : "bg-emerald-500"}`} /> {clockStatus}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* KPI Bulan Ini */}
          <div className="px-4 mt-3">
            <button data-testid="dash-kpi" onClick={() => setScreen({ name: "penilaian" })} className={`${cardM} w-full p-4 flex items-center gap-3 text-left`}>
              <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><BarChart3 className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-slate-400">KPI Bulan Ini</div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-extrabold text-blue-700" data-testid="dash-kpi-score">{dash?.kpi ? `${dash.kpi.score}/100` : "—"}</span>
                  <span className="flex items-center" data-testid="dash-kpi-stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-4 h-4 ${dash?.kpi && i <= dash.kpi.stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    ))}
                  </span>
                </div>
                {dash?.kpi && <div className="text-[10px] text-slate-400">{dash.kpi.label}</div>}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </button>
          </div>

          {/* Menu Karyawan */}
          <div className="px-4 mt-4">
            <div className="text-sm font-bold text-slate-900 mb-3">Menu Karyawan</div>
            <div className="grid grid-cols-4 gap-3">
              {MENUS.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.tid} data-testid={m.tid} onClick={() => setScreen({ name: m.screen })}
                    className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                    <span className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center"><Icon className="w-6 h-6" /></span>
                    <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {screen.name === "lainnya" && (
        <div data-testid="screen-lainnya">
          <SubHeader title="Lainnya" onBack={() => setScreen({ name: "dashboard" })} testid="lainnya-header" />
          <div className="px-4 space-y-2.5 pb-6">
            {MORE.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.tid} data-testid={m.tid} onClick={() => setScreen({ name: m.screen })}
                  className={`${cardM} w-full p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-transform`}>
                  <span className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
                  <span className="flex-1 text-sm font-bold text-slate-800">{m.label}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              );
            })}
          </div>
        </div>
      )}
      {screen.name === "absen" && <AbsenScreen onBack={() => setScreen({ name: "dashboard" })} onDone={reloadDash} />}
      {screen.name === "izin" && <IzinScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "kasbon" && <KasbonScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "saldo" && <SaldoScreen onBack={() => setScreen({ name: "dashboard" })} onOpenMutasi={() => setScreen({ name: "mutasi" })} />}
      {screen.name === "mutasi" && <MutasiScreen onBack={() => setScreen({ name: "saldo" })} />}
      {screen.name === "gaji" && <GajiScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "lembur" && <LemburScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "penilaian" && <PenilaianScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "shift" && <ShiftScreen onBack={() => setScreen({ name: "dashboard" })} myShift={myShift} />}
      {screen.name === "spk" && <SpkScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "pengumuman" && <PengumumanScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "sp" && <SpScreen onBack={() => setScreen({ name: "dashboard" })} />}
      {screen.name === "profil" && <ProfileScreen onBack={() => setScreen({ name: "dashboard" })} />}

      {screen.name === "dashboard" && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-slate-100 px-8 py-2 flex justify-between z-40">
          <button className="flex flex-col items-center text-blue-600" data-testid="nav-home"><Home className="w-5 h-5" /><span className="text-[10px] font-bold mt-0.5">Beranda</span></button>
          <button onClick={() => setScreen({ name: "pengumuman" })} className="flex flex-col items-center text-slate-400" data-testid="nav-notif"><Megaphone className="w-5 h-5" /><span className="text-[10px] font-bold mt-0.5">Pengumuman</span></button>
          <button onClick={() => setScreen({ name: "absen" })} className="flex flex-col items-center text-slate-400" data-testid="nav-absen"><Fingerprint className="w-5 h-5" /><span className="text-[10px] font-bold mt-0.5">Absen</span></button>
          <button onClick={() => setScreen({ name: "profil" })} className="flex flex-col items-center text-slate-400" data-testid="nav-profil"><User2Icon /><span className="text-[10px] font-bold mt-0.5">Profil</span></button>
        </div>
      )}
    </div>
  );
}

function User2Icon() {
  return <span className="w-5 h-5 rounded-full border-2 border-current flex items-end justify-center overflow-hidden"><span className="w-2.5 h-2.5 rounded-full bg-current translate-y-0.5" /></span>;
}

/* ==================== ABSEN (wajib selfie + GPS) ==================== */
function AbsenScreen({ onBack, onDone }) {
  const { user } = useAuth();
  const [att, setAtt] = useState([]);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(null);
  const [gpsMsg, setGpsMsg] = useState("");
  const [cam, setCam] = useState(null);
  const load = useCallback(() => {
    api.get("/attendance/me").then((r) => setAtt(r.data)).catch(() => {});
    api.get("/attendance/today-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); grabGps(); }, [load]);
  const record = info?.record;
  const grabGps = () => new Promise((resolve) => {
    if (!navigator.geolocation) { setGpsMsg("GPS tidak tersedia di perangkat ini"); return resolve(null); }
    navigator.geolocation.getCurrentPosition(
      (p) => { const v = { lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.round(p.coords.accuracy) }; setPos(v); setGpsMsg(""); resolve(v); },
      () => { setGpsMsg("Izin lokasi ditolak — GPS wajib aktif untuk absen"); resolve(null); },
      { enableHighAccuracy: true, timeout: 10000 });
  });
  const clock = (type) => {
    if (!pos) { toast.error("GPS wajib aktif — tekan 'Aktifkan / Segarkan GPS' lalu izinkan lokasi"); grabGps(); return; }
    setCam({ type });
  };
  const doClock = async (type, selfieFile) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selfieFile, `selfie-${type}-${Date.now()}.jpg`);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/attendance/clock", { type, lat: pos.lat, lng: pos.lng, selfie_url: up.data.url });
      toast.success(type === "in" ? "Absen masuk berhasil" : "Absen pulang berhasil");
      setCam(null); load(); onDone && onDone();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Gagal absen"); } finally { setBusy(false); }
  };
  const badge = (a) => a.off_schedule ? ["Luar Jadwal", "bg-sky-100 text-sky-700"] : a.status === "terlambat" ? [`Terlambat ${a.late_minutes || ""} mnt`, "bg-amber-100 text-amber-700"] : ["Hadir", "bg-emerald-100 text-emerald-700"];
  return (
    <div data-testid="screen-absen">
      <SubHeader title="Absensi" onBack={onBack} testid="absen-header" />
      <div className="px-4 space-y-3.5 pb-6">
        <div className={`${cardM} p-4`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Jadwal Hari Ini ({DAY_NAME[info?.day_key] || ""})</div>
          {info ? (info.is_workday
            ? <div className="font-bold text-slate-900 text-base mt-1">{user?.role_label} • {info.shift_start} – {info.shift_end}</div>
            : <div className="font-bold text-sky-600 text-base mt-1">LIBUR — absen tetap bisa, ditandai &quot;Luar Jadwal&quot;</div>)
            : <div className="text-sm text-slate-400 mt-1">Memuat jadwal...</div>}
        </div>
        <div className={`${cardM} p-4 space-y-3`}>
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <button data-testid="absen-gps-btn" onClick={grabGps} className="font-semibold text-blue-600">Aktifkan / Segarkan GPS</button>
            <span>{pos ? `Akurasi ±${pos.acc} m` : gpsMsg || "GPS belum aktif"}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="absen-clock-in-btn" disabled={busy || !pos || !!record?.clock_in} onClick={() => clock("in")} className="h-12 rounded-xl text-white font-bold text-sm disabled:opacity-40" style={{ background: BLUE }}>{record?.clock_in ? `Masuk ${formatTimeID(record.clock_in)}` : "Absen Masuk"}</button>
            <button data-testid="absen-clock-out-btn" disabled={busy || !pos || !record?.clock_in || !!record?.clock_out} onClick={() => clock("out")} className="h-12 rounded-xl bg-amber-500 text-white font-bold text-sm disabled:opacity-40">{record?.clock_out ? `Pulang ${formatTimeID(record.clock_out)}` : "Absen Pulang"}</button>
          </div>
          <p className="text-[10px] text-slate-400 text-center">Selfie + GPS wajib untuk absen.</p>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-400 mb-2">Riwayat 30 hari</div>
          <div className={`${cardM} divide-y divide-slate-50 overflow-hidden`}>
            {att.slice(0, 10).map((a) => { const [lb, cls] = badge(a); return (
              <div key={a.id} className="px-3.5 py-2.5 flex justify-between items-center">
                <div><div className="text-xs font-semibold text-slate-700">{formatDateID(a.date)}</div><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{lb}</span></div>
                <div className="text-[11px] text-slate-500">{a.clock_in ? formatTimeID(a.clock_in) : "—"} → {a.clock_out ? formatTimeID(a.clock_out) : "—"}</div>
              </div>); })}
            {!att.length && <div className="py-6 text-center text-xs text-slate-400">Belum ada riwayat absensi.</div>}
          </div>
        </div>
      </div>
      {cam && <SelfieCamera type={cam.type} busy={busy} onCapture={(f) => doClock(cam.type, f)} onClose={() => !busy && setCam(null)} />}
    </div>
  );
}

function SelfieCamera({ type, busy, onCapture, onClose }) {
  const videoRef = { current: null };
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [stream, setStream] = useState(null);
  useEffect(() => {
    let alive = true; let s = null;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: false })
      .then((st) => { if (!alive) { st.getTracks().forEach((t) => t.stop()); return; } s = st; setStream(st); })
      .catch(() => setErr("Kamera tidak tersedia / izin ditolak"));
    return () => { alive = false; s?.getTracks().forEach((t) => t.stop()); };
  }, []);
  useEffect(() => { if (stream && videoRef.current) videoRef.current.srcObject = stream; }, [stream]);
  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => { if (b) setPreview({ url: URL.createObjectURL(b), blob: b }); }, "image/jpeg", 0.85);
  };
  const retake = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  return (
    <div data-testid="selfie-modal" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-md rounded-3xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm">Selfie Absen {type === "in" ? "Masuk" : "Pulang"}</h3>
          <button onClick={() => !busy && onClose()} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        {err ? (
          <div className="aspect-square rounded-2xl bg-slate-100 flex flex-col items-center justify-center text-slate-400 text-xs p-6 text-center"><Camera className="w-8 h-8 mb-2" />{err}</div>
        ) : preview ? (
          <img src={preview.url} alt="Selfie" className="w-full aspect-square object-cover rounded-2xl bg-slate-100" data-testid="selfie-preview" />
        ) : (
          <video ref={(el) => (videoRef.current = el)} autoPlay playsInline muted className="w-full aspect-square object-cover rounded-2xl bg-slate-900" data-testid="selfie-video" />
        )}
        <div className="grid grid-cols-2 gap-2">
          {preview ? (
            <>
              <button data-testid="selfie-retake" onClick={retake} disabled={busy} className="h-11 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold disabled:opacity-40">Ambil Ulang</button>
              <button data-testid="selfie-submit" onClick={() => onCapture(preview.blob)} disabled={busy} className="h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: BLUE }}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Gunakan Foto</button>
            </>
          ) : (
            <button data-testid="selfie-capture" onClick={capture} disabled={!!err} className="col-span-2 h-11 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: BLUE }}><Camera className="w-4 h-4" /> Ambil Foto</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== IZIN ==================== */
function IzinScreen({ onBack }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/leave/me").then((r) => setData(r.data)).catch(() => setData({ items: [], balance: null })), []);
  useEffect(() => { load(); }, [load]);
  const ST = { diajukan: ["Menunggu", "bg-amber-50 text-amber-600"], menunggu: ["Menunggu", "bg-amber-50 text-amber-600"], disetujui: ["Disetujui", "bg-emerald-50 text-emerald-600"], ditolak: ["Ditolak", "bg-red-50 text-red-600"] };
  return (
    <div data-testid="screen-izin">
      <SubHeader title="Izin & Cuti" onBack={onBack} testid="izin-header" />
      <div className="px-4 space-y-3 pb-6">
        <button data-testid="izin-new-btn" onClick={() => setShowForm(true)} className="w-full h-12 rounded-2xl text-white text-sm font-bold" style={{ background: BLUE }}>+ Ajukan Izin</button>
        {(data?.items || []).map((l) => { const [lb, cls] = ST[l.status] || [l.status, "bg-slate-100 text-slate-500"]; return (
          <div key={l.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-center"><span className="text-sm font-bold text-slate-800 capitalize">{l.type}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lb}</span></div>
            <div className="text-xs text-slate-500 mt-1">{formatDateID(l.start_date)} — {formatDateID(l.end_date)} • {l.reason || "-"}</div>
          </div>); })}
        {data !== null && !(data?.items || []).length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada pengajuan izin.</div>}
      </div>
      {showForm && <IzinForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} busy={busy} setBusy={setBusy} />}
    </div>
  );
}

function IzinForm({ onClose, onSaved, busy, setBusy }) {
  const [f, setF] = useState({ type: "izin", start_date: "", end_date: "", reason: "" });
  const submit = async () => {
    if (!f.start_date || !f.end_date) return toast.error("Tanggal wajib diisi");
    setBusy(true);
    try { await api.post("/leave", f); toast.success("Izin diajukan"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="izin-modal" className="fixed inset-0 z-50 bg-slate-900/60 flex items-end justify-center" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-md rounded-t-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-slate-900 text-base">Ajukan Izin</h3>
        <select data-testid="izin-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inpM}>
          <option value="izin">Izin</option><option value="cuti">Cuti</option><option value="sakit">Sakit</option>
        </select>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[10px] text-slate-500">Dari</label><input data-testid="izin-from" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inpM} /></div>
          <div><label className="text-[10px] text-slate-500">Sampai</label><input data-testid="izin-to" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={inpM} /></div>
        </div>
        <textarea data-testid="izin-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={2} placeholder="Alasan" className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
        <button data-testid="izin-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-bold disabled:opacity-40" style={{ background: BLUE }}>{busy ? "Mengirim..." : "Ajukan"}</button>
      </div>
    </div>
  );
}

/* ==================== KASBON ==================== */
function KasbonScreen({ onBack }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ amount: "", installments: "1", reason: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/kasbon/me").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const ST = { diajukan: ["Menunggu Persetujuan", "bg-amber-50 text-amber-600"], disetujui: ["Disetujui", "bg-emerald-50 text-emerald-600"], ditolak: ["Ditolak", "bg-red-50 text-red-600"], lunas: ["Lunas", "bg-sky-50 text-sky-600"], belum_lunas: ["Belum Lunas", "bg-orange-50 text-orange-600"] };
  const submit = async () => {
    if (!(parseInt(form.amount) >= 10000)) return toast.error("Nominal minimal Rp10.000");
    setBusy(true);
    try { await api.post("/kasbon", { amount: parseInt(form.amount), installments: parseInt(form.installments) || 1, reason: form.reason }); toast.success("Kasbon diajukan ke HR"); setShowForm(false); setForm({ amount: "", installments: "1", reason: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="screen-kasbon">
      <SubHeader title="Kasbon" onBack={onBack} testid="kasbon-header" />
      <div className="px-4 space-y-3 pb-6">
        <button data-testid="kasbon-new-btn" onClick={() => setShowForm(true)} className="w-full h-12 rounded-2xl text-white text-sm font-bold" style={{ background: BLUE }}>+ Ajukan Kasbon</button>
        {(items || []).map((k) => { const [lb, cls] = ST[k.status] || [k.status, "bg-slate-100 text-slate-500"]; return (
          <div key={k.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-center"><span className="text-sm font-bold text-slate-800">{formatIDR(k.amount)}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lb}</span></div>
            <div className="text-xs text-slate-500 mt-1">Angsuran {k.installments}× • terbayar {k.paid_count || 0}× • {k.reason || "-"}</div>
          </div>); })}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada kasbon.</div>}
      </div>
      {showForm && (
        <div data-testid="kasbon-modal" className="fixed inset-0 z-50 bg-slate-900/60 flex items-end justify-center" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-md rounded-t-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900 text-base">Ajukan Kasbon</h3>
            <input data-testid="kasbon-amount" type="number" min="10000" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Nominal (Rp) — minimal Rp10.000" className={inpM} />
            <input data-testid="kasbon-installments" type="number" min="1" max="24" value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} placeholder="Jumlah angsuran (bulan)" className={inpM} />
            <textarea data-testid="kasbon-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="Alasan" className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400" />
            <button data-testid="kasbon-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-bold disabled:opacity-40" style={{ background: BLUE }}>{busy ? "Mengirim..." : "Ajukan"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== GAJI & UPAH ==================== */
function GajiScreen({ onBack }) {
  const [items, setItems] = useState([]);
  const [wages, setWages] = useState(null);
  useEffect(() => {
    api.get("/payroll/me").then((r) => setItems(r.data)).catch(() => {});
    api.get("/prod/wages/me").then((r) => setWages(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="screen-gaji">
      <SubHeader title="Gaji & Upah" onBack={onBack} testid="gaji-header" />
      <div className="px-4 space-y-3 pb-6">
        {wages && (
          <div className="rounded-2xl p-4 text-white" style={{ background: GRAD }}>
            <div className="text-[11px] text-blue-100">Saldo Upah</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="gaji-saldo">{formatIDR(wages.saldo || 0)}</div>
            <div className="text-[11px] text-blue-100 mt-1">Upah yang belum dicairkan</div>
          </div>
        )}
        <div className="text-[11px] font-semibold text-slate-400">Slip Gaji</div>
        {items.map((p) => (
          <div key={p.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-start mb-2">
              <div><div className="text-[10px] text-slate-400 uppercase">Periode</div><div className="text-sm font-semibold text-slate-800">{formatDateID(p.period_start)} — {formatDateID(p.period_end)}</div></div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-semibold capitalize">{p.status?.replace(/_/g, " ")}</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Gaji Pokok</span><span>{formatIDR(p.basic)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Lembur</span><span>{formatIDR(p.overtime)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Bonus</span><span className="text-emerald-600">+{formatIDR(p.bonus)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Potongan</span><span className="text-red-500">-{formatIDR(p.deduction)}</span></div>
              <div className="flex justify-between font-bold pt-2 border-t border-slate-50 text-sm"><span>Netto</span><span className="text-slate-900">{formatIDR(p.net)}</span></div>
            </div>
          </div>
        ))}
        {!items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada slip gaji.</div>}
      </div>
    </div>
  );
}

/* ==================== LEMBUR ==================== */
function LemburScreen({ onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/overtime/me").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  return (
    <div data-testid="screen-lembur">
      <SubHeader title="Lembur" onBack={onBack} testid="lembur-header" />
      <div className="px-4 space-y-3 pb-6">
        {data && (
          <div className="rounded-2xl p-4 text-white" style={{ background: GRAD }}>
            <div className="text-[11px] text-blue-100">Total Lembur Bulan Ini</div>
            <div className="text-2xl font-bold mt-0.5" data-testid="lembur-total">{formatIDR(data.month_total || 0)}</div>
            <div className="text-[11px] text-blue-100 mt-1">{Math.round((data.month_minutes || 0) / 60 * 10) / 10} jam</div>
          </div>
        )}
        {(data?.items || []).map((o) => (
          <div key={o.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-center"><span className="text-sm font-bold text-slate-800">{formatDateID(o.date)}</span><span className="text-xs font-bold text-blue-600">{formatIDR(o.amount)}</span></div>
            <div className="text-xs text-slate-500 mt-1">{Math.round((o.minutes || 0) / 60 * 10) / 10} jam • tarif {formatIDR(o.rate)}/jam</div>
          </div>
        ))}
        {data !== null && !(data?.items || []).length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada lembur disetujui.</div>}
      </div>
    </div>
  );
}

/* ==================== PENILAIAN ==================== */
function PenilaianScreen({ onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/appraisals/me").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div data-testid="screen-penilaian">
      <SubHeader title="Penilaian Kinerja" onBack={onBack} testid="penilaian-header" />
      <div className="px-4 space-y-3 pb-6">
        {(items || []).map((a) => (
          <div key={a.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-bold text-slate-800">Periode {a.period}</span>
              <span className="text-base font-bold text-blue-600" data-testid={`appraisal-score-${a.id}`}>{a.final_score ?? "—"} <span className="text-xs text-slate-400">({a.grade || "-"})</span></span>
            </div>
            {a.kelebihan && <div className="text-xs text-slate-500"><span className="font-semibold text-emerald-600">Kelebihan:</span> {a.kelebihan}</div>}
            {a.perlu_ditingkatkan && <div className="text-xs text-slate-500 mt-0.5"><span className="font-semibold text-amber-600">Perlu ditingkatkan:</span> {a.perlu_ditingkatkan}</div>}
          </div>
        ))}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada penilaian.</div>}
      </div>
    </div>
  );
}

/* ==================== SHIFT & JADWAL ==================== */
function ShiftScreen({ onBack, myShift }) {
  return (
    <div data-testid="screen-shift">
      <SubHeader title="Shift & Jadwal" onBack={onBack} testid="shift-header" />
      <div className="px-4 space-y-3 pb-6">
        <div className="rounded-2xl p-4 text-white" style={{ background: GRAD }}>
          <div className="text-[11px] text-blue-100">Shift Hari Ini</div>
          <div className="text-2xl font-bold mt-0.5" data-testid="shift-today">{myShift?.today ? `${myShift.today.shift_start} – ${myShift.today.shift_end}` : "—"}</div>
          <div className="text-[11px] text-blue-100 mt-1">{myShift?.today?.shift_name || ""} • toleransi telat {myShift?.today?.late_tolerance ?? 10} mnt</div>
        </div>
        <div className="text-[11px] font-semibold text-slate-400">Jadwal 7 Hari Ke Depan</div>
        <div className={`${cardM} divide-y divide-slate-50 overflow-hidden`}>
          {(myShift?.week || []).map((d) => (
            <div key={d.date} className="px-3.5 py-2.5 flex justify-between items-center" data-testid={`shift-day-${d.date}`}>
              <div><div className="text-xs font-semibold text-slate-700">{DAY_NAME[d.day_key]}</div><div className="text-[10px] text-slate-400">{formatDateID(d.date)}</div></div>
              <div className={`text-[11px] font-bold ${d.is_workday ? "text-blue-600" : "text-slate-400"}`}>{d.is_workday ? `${d.shift_start} – ${d.shift_end}` : "LIBUR"}</div>
            </div>
          ))}
          {!(myShift?.week || []).length && <div className="py-6 text-center text-xs text-slate-400">Memuat jadwal...</div>}
        </div>
      </div>
    </div>
  );
}

/* ==================== TUGAS SPK ==================== */
function SpkScreen({ onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/spk/me").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const ST = { selesai: ["Selesai", "bg-emerald-50 text-emerald-600"], proses: ["Proses", "bg-blue-50 text-blue-600"], belum: ["Belum", "bg-slate-100 text-slate-500"] };
  return (
    <div data-testid="screen-spk">
      <SubHeader title="Tugas SPK" onBack={onBack} testid="spk-header" />
      <div className="px-4 space-y-3 pb-6">
        {(items || []).map((s) => { const [lb, cls] = ST[s.my_status] || [s.my_status || s.status, "bg-slate-100 text-slate-500"]; return (
          <div key={s.id} className={`${cardM} p-4`}>
            <div className="flex justify-between items-center"><span className="text-sm font-bold text-slate-800">{s.title}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lb}</span></div>
            <div className="text-xs text-slate-500 mt-1">{s.spk_no} • deadline {s.deadline ? formatDateID(s.deadline.slice(0, 10)) : "—"}</div>
            {s.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{s.description}</div>}
          </div>); })}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada tugas SPK.</div>}
      </div>
    </div>
  );
}

/* ==================== PENGUMUMAN ==================== */
function PengumumanScreen({ onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/announcements").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div data-testid="screen-pengumuman">
      <SubHeader title="Pengumuman" onBack={onBack} testid="pengumuman-header" />
      <div className="px-4 space-y-3 pb-6">
        {(items || []).map((a) => (
          <div key={a.id} className={`${cardM} p-4`}>
            <div className="text-sm font-bold text-slate-800">{a.title}</div>
            <div className="text-xs text-slate-500 mt-1 whitespace-pre-line">{a.body}</div>
            <div className="text-[10px] text-slate-400 mt-1.5">{formatDateID(a.created_at)}</div>
          </div>
        ))}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Belum ada pengumuman.</div>}
      </div>
    </div>
  );
}

/* ==================== SURAT PERINGATAN ==================== */
function SpScreen({ onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/warnings/me").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div data-testid="screen-sp">
      <SubHeader title="Surat Peringatan" onBack={onBack} testid="sp-header" />
      <div className="px-4 space-y-3 pb-6">
        {(items || []).map((w) => (
          <div key={w.id} className={`${cardM} p-4 border-l-4 ${w.level === 3 ? "border-red-500" : w.level === 2 ? "border-amber-500" : "border-sky-500"}`}>
            <div className="flex justify-between items-center"><span className="text-sm font-bold text-slate-800">SP {w.level}</span><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold capitalize">{w.status}</span></div>
            <div className="text-xs text-slate-500 mt-1">{w.reason || w.description || "-"}</div>
            <div className="text-[10px] text-slate-400 mt-1.5">{formatDateID(w.issued_date)}</div>
          </div>
        ))}
        {items !== null && !items.length && <div className={`${cardM} py-8 text-center text-xs text-slate-400`}>Tidak ada surat peringatan. Pertahankan!</div>}
      </div>
    </div>
  );
}
